import React, { useEffect, useMemo, useState, useRef } from 'react';
import { MasterFlightRecord, formatDisplayDate } from '../../../../../../../utils/types';
import { getIataDayFromDate } from '../../../../../../../utils/types/validation';
import { FormInput, SeasonInput, FormSection } from '../../../../../common';
import { FrequencyDaySelector } from '../../FrequencyDaySelector';
import { computeLocalFrequency, getMYTDayShift, shiftDateByDays, unshiftDateByDays } from '../../../../../../../utils/timeUtils';
import { useTimezone } from '../../../../../../../contexts/TimezoneContext';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useLazyGetSeasonFromDateQuery, type SeasonInfo } from '../../../../../../../services/redux/api';

type UpdateDraftFn = <K extends keyof MasterFlightRecord>(
  key: K,
  value: MasterFlightRecord[K]
) => void;

interface SchedulePeriodSectionProps {
  draft: MasterFlightRecord;
  updateDraft: UpdateDraftFn;
  setDraft: React.Dispatch<React.SetStateAction<MasterFlightRecord>>;
  isVariantMode: boolean;
  isAdhocMode?: boolean;
  parentDateRange?: { startDate: string; endDate: string } | null;
  getFieldError: (field: keyof MasterFlightRecord) => string | null;
  handleBlur: (field: keyof MasterFlightRecord) => void;
  revalidateIfTouched: (field: keyof MasterFlightRecord, value: MasterFlightRecord[keyof MasterFlightRecord]) => void;
}

/**
 * Returns the set of IATA days (1-7) that actually occur in [start, end].
 * For ranges >= 7 days every day-of-week appears, so returns null (= all valid).
 */
function getValidDaysInRange(startDate: string, endDate: string): Set<string> | null {
  if (!startDate || !endDate) return null;
  const [sy, sm, sd] = startDate.split('-').map(Number);
  const [ey, em, ed] = endDate.split('-').map(Number);
  const start = new Date(sy, sm - 1, sd);
  const end = new Date(ey, em - 1, ed);
  const diffDays = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays >= 6) return null; // 7+ dates always cover all 7 days
  if (diffDays < 0) return null;

  const valid = new Set<string>();
  const cur = new Date(start);
  while (cur <= end) {
    const jsDay = cur.getDay(); // 0=Sun
    valid.add(String(jsDay === 0 ? 7 : jsDay));
    cur.setDate(cur.getDate() + 1);
  }
  return valid;
}

/**
 * Schedule Period Section
 * Contains: Start Date, End Date, Season ID, Frequency
 */
export function SchedulePeriodSection({
  draft,
  updateDraft,
  setDraft,
  isVariantMode,
  isAdhocMode,
  parentDateRange,
  getFieldError,
  handleBlur,
  revalidateIfTouched,
}: SchedulePeriodSectionProps) {
  const [triggerGetSeason] = useLazyGetSeasonFromDateQuery();
  const [endDateSeason, setEndDateSeason] = useState<SeasonInfo | null>(null);
  const [startDateSeason, setStartDateSeason] = useState<SeasonInfo | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const calculationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [adhocDateError, setAdhocDateError] = useState<{ start?: string; end?: string }>({});

  // Validate adhoc dates against parent range
  const validateAdhocDate = (field: 'start' | 'end', value: string) => {
    if (!isAdhocMode || !parentDateRange || !value) {
      setAdhocDateError(prev => ({ ...prev, [field]: undefined }));
      return;
    }
    const parentStart = parentDateRange.startDate.slice(0, 10);
    const parentEnd = parentDateRange.endDate.slice(0, 10);
    const dateValue = value.slice(0, 10);
    if (field === 'start' && dateValue < parentStart) {
      setAdhocDateError(prev => ({ ...prev, start: `Must be on or after ${formatDisplayDate(parentStart)}` }));
    } else if (field === 'end' && dateValue > parentEnd) {
      setAdhocDateError(prev => ({ ...prev, end: `Must be on or before ${formatDisplayDate(parentEnd)}` }));
    } else {
      setAdhocDateError(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Timezone-aware date display: show MYT-shifted dates, store UTC
  const { timezone } = useTimezone();
  const isMYT = timezone === 'MYT';
  const dayShift = isMYT ? getMYTDayShift(draft.stations?.[0]?.std) : 0;
  const displayStartDate = shiftDateByDays(draft.startDate, dayShift) || '';
  const displayEndDate = shiftDateByDays(draft.endDate, dayShift) || '';

  // UTC locked days: used by the auto-add effect (works with draft.frequency which is UTC)
  // Adhoc flights skip boundary-day validation, so no days are locked
  const lockedDaysUTC = useMemo(() => {
    if (isAdhocMode) return [];
    const days = new Set<string>();
    if (draft.startDate) days.add(getIataDayFromDate(draft.startDate));
    if (draft.endDate) days.add(getIataDayFromDate(draft.endDate));
    return Array.from(days);
  }, [draft.startDate, draft.endDate, isAdhocMode]);

  // Display locked days: shifted to MYT when applicable, for the FrequencyDaySelector UI
  const lockedDays = useMemo(() => {
    if (isAdhocMode) return [];
    if (!dayShift) return lockedDaysUTC;
    const days = new Set<string>();
    if (displayStartDate) days.add(getIataDayFromDate(displayStartDate));
    if (displayEndDate) days.add(getIataDayFromDate(displayEndDate));
    return Array.from(days);
  }, [lockedDaysUTC, dayShift, displayStartDate, displayEndDate, isAdhocMode]);

  // Ensure locked days are always present in the frequency string (UTC domain)
  // Handles initial state (new record where dates are set but frequency is empty)
  // and any edge case where dates change without going through the handlers
  useEffect(() => {
    if (lockedDaysUTC.length === 0) return;
    const currentFreq = draft.frequency || '';
    const missing = lockedDaysUTC.filter(d => !currentFreq.includes(d));
    if (missing.length > 0) {
      const updatedFreq = [...currentFreq.split(''), ...missing]
        .filter(d => d >= '1' && d <= '7')
        .sort()
        .join('');
      updateDraft('frequency', updatedFreq);
    }
  }, [lockedDaysUTC]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-calculate seasonId when end date changes (debounced)
  useEffect(() => {
    // Clear previous timeout
    if (calculationTimeoutRef.current) {
      clearTimeout(calculationTimeoutRef.current);
    }

    if (!draft.endDate) {
      setEndDateSeason(null);
      setIsCalculating(false);
      return;
    }

    setIsCalculating(true);

    // Debounce API call (300ms)
    calculationTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await triggerGetSeason(draft.endDate).unwrap();
        setEndDateSeason(result);

        // Auto-populate seasonId based on end date ONLY if override is not enabled
        if (!draft.overrideSeasonValidation && result.seasonId !== draft.seasonId) {
          updateDraft('seasonId', result.seasonId);
        }
      } catch (error) {
        console.error('Failed to calculate season from end date:', error);
        setEndDateSeason(null);
      } finally {
        setIsCalculating(false);
      }
    }, 300);

    return () => {
      if (calculationTimeoutRef.current) {
        clearTimeout(calculationTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.endDate, draft.overrideSeasonValidation]);

  // Fetch start date season for multi-season indicator
  useEffect(() => {
    if (!draft.startDate) {
      setStartDateSeason(null);
      return;
    }

    triggerGetSeason(draft.startDate)
      .unwrap()
      .then(setStartDateSeason)
      .catch(() => setStartDateSeason(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.startDate]);

  // Handle Start Date change with real-time validation
  const handleStartDateChange = (value: string) => {
    validateAdhocDate('start', value);
    updateDraft('startDate', value);
    revalidateIfTouched('startDate', value);

    // Remove old start-date day from frequency (if no longer needed) and add new one
    let freq = draft.frequency || '';
    const oldDay = draft.startDate ? getIataDayFromDate(draft.startDate) : null;
    const endDay = draft.endDate ? getIataDayFromDate(draft.endDate) : null;
    const newDay = value ? getIataDayFromDate(value) : null;

    // Remove old day if it changed and isn't still needed by the end date
    if (oldDay && oldDay !== newDay && oldDay !== endDay) {
      freq = freq.split('').filter(d => d !== oldDay).join('');
    }
    // Add new day if not already present
    if (newDay && !freq.includes(newDay)) {
      freq = [...freq.split(''), newDay].filter(d => d >= '1' && d <= '7').sort().join('');
    }
    // Strip days that can't occur in the new date range (e.g. single-day ranges)
    const validDaysStart = getValidDaysInRange(value, draft.endDate || '');
    if (validDaysStart) {
      freq = freq.split('').filter(d => validDaysStart.has(d)).join('');
    }
    if (freq !== (draft.frequency || '')) {
      updateDraft('frequency', freq);
      revalidateIfTouched('frequency', freq);
    }

    // Also re-validate end date since it depends on start date
    if (draft.endDate) {
      revalidateIfTouched('endDate', draft.endDate);
    }

    // Re-validate frequency since zero-dates check depends on date range
    if (draft.frequency) {
      revalidateIfTouched('frequency', draft.frequency);
    }
  };

  // Handle End Date change with real-time validation
  const handleEndDateChange = (value: string) => {
    validateAdhocDate('end', value);
    updateDraft('endDate', value);
    revalidateIfTouched('endDate', value);

    // Remove old end-date day from frequency (if no longer needed) and add new one
    let freq = draft.frequency || '';
    const oldDay = draft.endDate ? getIataDayFromDate(draft.endDate) : null;
    const startDay = draft.startDate ? getIataDayFromDate(draft.startDate) : null;
    const newDay = value ? getIataDayFromDate(value) : null;

    // Remove old day if it changed and isn't still needed by the start date
    if (oldDay && oldDay !== newDay && oldDay !== startDay) {
      freq = freq.split('').filter(d => d !== oldDay).join('');
    }
    // Add new day if not already present
    if (newDay && !freq.includes(newDay)) {
      freq = [...freq.split(''), newDay].filter(d => d >= '1' && d <= '7').sort().join('');
    }
    // Strip days that can't occur in the new date range (e.g. single-day ranges)
    const validDaysEnd = getValidDaysInRange(draft.startDate || '', value);
    if (validDaysEnd) {
      freq = freq.split('').filter(d => validDaysEnd.has(d)).join('');
    }
    if (freq !== (draft.frequency || '')) {
      updateDraft('frequency', freq);
      revalidateIfTouched('frequency', freq);
    }

    // Re-validate frequency since zero-dates check depends on date range
    if (draft.frequency) {
      revalidateIfTouched('frequency', draft.frequency);
    }
  };

  // Handle Season ID change
  const handleSeasonIdChange = (value: string) => {
    // SeasonInput already handles uppercase transformation
    updateDraft('seasonId', value);
    revalidateIfTouched('seasonId', value);
  };

  // Check if flight spans multiple seasons
  const spansMultipleSeasons = startDateSeason && endDateSeason &&
    startDateSeason.seasonId !== endDateSeason.seasonId;

  return (
    <FormSection title="Schedule Period" icon={<CalendarMonthIcon sx={{ fontSize: 20 }} />} collapsible defaultExpanded>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-x-3">
          {/* Start Date */}
          <FormInput
            label={isMYT && dayShift ? 'Start Date (MYT)' : 'Start Date'}
            value={displayStartDate}
            onChange={(value) => handleStartDateChange(dayShift ? unshiftDateByDays(value, dayShift) : value)}
            onBlur={() => handleBlur('startDate')}
            type="date"
            required
            error={adhocDateError.start || getFieldError('startDate')}
            min={parentDateRange?.startDate || undefined}
            max={parentDateRange?.endDate || undefined}
          />

          {/* End Date */}
          <FormInput
            label={isMYT && dayShift ? 'End Date (MYT)' : 'End Date'}
            value={displayEndDate}
            onChange={(value) => handleEndDateChange(dayShift ? unshiftDateByDays(value, dayShift) : value)}
            onBlur={() => handleBlur('endDate')}
            type="date"
            min={parentDateRange?.startDate || displayStartDate || undefined}
            max={parentDateRange?.endDate || undefined}
            required
            error={adhocDateError.end || getFieldError('endDate')}
          />

          {/* Season ID - Auto-calculated from end date, editable with override */}
          <div className="relative">
            <SeasonInput
              label="Season ID"
              value={draft.seasonId}
              onChange={handleSeasonIdChange}
              onBlur={() => handleBlur('seasonId')}
              error={getFieldError('seasonId')}
              required
              disabled={isVariantMode || !draft.overrideSeasonValidation}
            />
            {draft.overrideSeasonValidation && (
              <div className="absolute right-2 top-9 text-xs text-warning-600 font-medium">
                Manual Override
              </div>
            )}
          </div>
        </div>

        {/* Adhoc date range constraint */}
        {isAdhocMode && parentDateRange && (
          <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <InfoIcon className="text-amber-600 flex-shrink-0" sx={{ fontSize: 16 }} />
            <p className="text-xs text-amber-800">
              Adhoc dates must fall within parent range: <strong>{formatDisplayDate(parentDateRange.startDate)}</strong> to <strong>{formatDisplayDate(parentDateRange.endDate)}</strong>
            </p>
          </div>
        )}

        {/* Multi-Season Span Indicator */}
        {spansMultipleSeasons && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <InfoIcon className="text-blue-600 mt-0.5" sx={{ fontSize: 18 }} />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-blue-900 mb-1">
                Multi-Season Flight
              </p>
              <p className="text-blue-800">
                This flight spans from{' '}
                <span className="font-semibold">
                  {startDateSeason?.seasonType} ({startDateSeason?.seasonId})
                </span>
                {' '}to{' '}
                <span className="font-semibold">
                  {endDateSeason?.seasonType} ({endDateSeason?.seasonId})
                </span>
                . Season ID is automatically set to <strong>{endDateSeason?.seasonId}</strong> based on the end date.
              </p>
            </div>
          </div>
        )}

        {/* Helper text for season ID - always visible to prevent layout shift */}
        <div className="text-xs flex items-center">
          {!draft.overrideSeasonValidation ? (
            <div className="text-gray-500 flex items-center justify-between w-full px-2">
              <span>Season ID is automatically calculated from the end date</span>
              <label className={`flex items-center gap-1.5 ${isVariantMode ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={draft.overrideSeasonValidation || false}
                  onChange={(e) => {
                    const isOverriding = e.target.checked;
                    if (isOverriding) {
                      updateDraft('overrideSeasonValidation', true);
                    } else {
                      // When disabling override, reset to calculated season
                      setDraft(prev => ({
                        ...prev,
                        overrideSeasonValidation: false,
                        seasonId: endDateSeason?.seasonId || prev.seasonId
                      }));
                    }
                  }}
                  disabled={isVariantMode}
                  className={`w-3.5 h-3.5 text-primary-500 bg-white border-gray-300 rounded focus:ring-2 focus:ring-primary-500 ${isVariantMode ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                />
                <span className="text-xs font-medium text-gray-600 hover:text-primary-600">
                  Override
                </span>
              </label>
            </div>
          ) : (
            <div className="px-2 py-1.5 bg-warning-50 border border-warning-200 rounded flex items-center justify-between w-full">
              <span className="text-warning-700 font-medium">
                <WarningAmberIcon sx={{ fontSize: 16 }} /> Manual override enabled - season validation bypassed
              </span>
              <button
                type="button"
                onClick={() => {
                  setDraft(prev => ({
                    ...prev,
                    overrideSeasonValidation: false,
                    seasonId: endDateSeason?.seasonId || prev.seasonId
                  }));
                }}
                disabled={isVariantMode}
                className="text-xs font-medium text-warning-700 hover:text-warning-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Disable Override
              </button>
            </div>
          )}
        </div>

        {/* Frequency Selector with Preview Button */}
        <FrequencyDaySelector
          frequency={draft.frequency}
          frequencyMYT={draft.frequencyMYT}
          onChange={v => {
            // Update both frequency (UTC) and frequencyMYT in one setDraft call
            const std = draft.stations?.[0]?.std;
            const offset = draft.stations?.[0]?.utcLocalTimeVariationDep || '+0800';
            const mytFreq = std ? computeLocalFrequency(v, std, offset) : undefined;
            setDraft(prev => ({
              ...prev,
              frequency: v,
              ...(mytFreq ? { frequencyMYT: mytFreq } : {}),
            }));
            revalidateIfTouched('frequency', v);
          }}
          onBlur={() => handleBlur('frequency')}
          error={getFieldError('frequency')}
          startDate={displayStartDate}
          endDate={displayEndDate}
          lockedDays={lockedDays}
          stdUTC={draft.stations?.[0]?.std}
          utcOffset={draft.stations?.[0]?.utcLocalTimeVariationDep ?? undefined}
        />
      </div>
    </FormSection>
  );
}
